/**
 * Landing on Today, the app's home, when the gardener arrives rather than
 * goes somewhere.
 *
 * Two ways in used to land on Plants:
 *
 * - Signing in. The sign-in screen stands in for whatever page was asked
 *   for, and once signed in that page shows. An old bookmark at "/" came
 *   back to Plants. Now signing in at "/" goes to Today; a link to any other
 *   page (a plant, the photos page, a bed, joining a garden, the
 *   subscription), its query string included, is kept, since that is where
 *   the gardener was going (B159).
 * - An icon added to the home screen before Today existed. The address it
 *   opens is kept from when it was added: /plants, for good on an iPhone
 *   and until Chrome updates it on Android. Now opening the installed app
 *   at /plants, fresh, goes to Today; a reload or a tap on the Plants tab is
 *   left alone.
 *
 * Who uses it: main.tsx (openOnHome, before rendering), LoginPage and
 * GoogleOneTap (markSigningIn) and Layout (afterSignIn).
 *
 * @module homePage
 */

import { LAUNCHED_SESSION_KEY, SIGNING_IN_SESSION_KEY } from '../../config/tunables/shared.ts';

/** The home page. */
export const HOME = '/today';

/** The home addresses (the old bookmark "/" and Today): only these land on Today after sign-in. Any other page is a link and is kept (B159). */
const MAIN_PAGES = new Set(['/', '/today']);

/** Marks a sign-in under way, across Google's redirect and back. */
const SIGNING_IN_KEY = SIGNING_IN_SESSION_KEY;

/** Marks this tab's session as launched, so a reload is not taken for a launch. */
const LAUNCHED_KEY = LAUNCHED_SESSION_KEY;

/** The address an installed icon opened before Today was the home. */
const OLD_START = '/plants';

/**
 * Whether the app is running as an installed app rather than in a browser tab.
 *
 * @returns True for a home-screen app (iOS's standalone, or display-mode standalone).
 */
function isInstalledApp(): boolean {
  try {
    const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/**
 * Sends an installed app opened at its old address to Today, before the app
 * draws. Only on a launch: the first load of the tab's session. Call once,
 * before rendering.
 */
export function openOnHome(): void {
  try {
    const launched = window.sessionStorage.getItem(LAUNCHED_KEY) !== null;
    window.sessionStorage.setItem(LAUNCHED_KEY, '1');
    if (!launched && isInstalledApp() && window.location.pathname === OLD_START && !window.location.search) {
      window.history.replaceState(window.history.state, '', HOME);
    }
  } catch {
    // Storage refused: the app opens where it was asked to, as before.
  }
}

/** Notes that the gardener is signing in, from the sign-in screen, just before it starts. */
export function markSigningIn(): void {
  try {
    window.sessionStorage.setItem(SIGNING_IN_KEY, '1');
  } catch {
    // Storage refused: they stay on the page they signed in on.
  }
}

/**
 * Where to go now that a sign-in has finished, once: Today when it was
 * started at "/" (the old bookmark), or null to stay (no sign-in was
 * under way, or it was on a page somebody was sent to on purpose).
 *
 * @param pathname - The page the sign-in finished on.
 * @returns HOME, or null to stay where they are.
 */
export function afterSignIn(pathname: string): string | null {
  try {
    if (window.sessionStorage.getItem(SIGNING_IN_KEY) === null) return null;
    window.sessionStorage.removeItem(SIGNING_IN_KEY);
  } catch {
    return null;
  }
  const page = pathname.replace(/\/+$/, '') || '/';
  return MAIN_PAGES.has(page) && page !== HOME ? HOME : null;
}
